import { useQueryClient } from 'react-query';
import { AudioLibraryTypes } from '@frontend/api-audio-library';
import { InlineTextEditor } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { ValidatorFieldState, useAlert } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { logError } from '../../utils/loggers';
import { fileExtensionRegex } from '../../utils/phone-utils';
import { useAudioLibraryMutations } from './use-audio-library-mutations';

interface EditMediaNameProps {
  phoneMedia: AudioLibraryTypes.AudioFileType;
  editEnabled?: boolean;
}

const maxFileNameLength = 32;

export const NewEditMediaName = ({ phoneMedia, editEnabled }: EditMediaNameProps) => {
  const { t } = useTranslation('phone');
  const alert = useAlert();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const { update } = useAudioLibraryMutations();
  const queryClient = useQueryClient();
  const phoneTenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const locationId = settingsTenantLocation?.locationId ?? '';
  const audioFileName = phoneMedia?.name?.replace(fileExtensionRegex, '');

  const updateFn = (payload: AudioLibraryTypes.RenameRequestType) =>
    update.mutate(payload, {
      onSuccess: () => {
        alert.success(t('Successfully updated Audio File.'));
        queryClient.invalidateQueries([phoneTenantId, ...queryKeys.audioPicker({})]);
        queryClient.invalidateQueries([locationId, ...queryKeys.phoneMedia()]);
      },
      onError: (error) => {
        alert.error(t('Error in updating Audio File.'));
        logError('NewEditMediaName:update | Failed to update audio file name', {
          data: { mediaItemId: phoneMedia.mediaItemId, phoneTenantId, editEnabled, error },
        });
      },
    });

  const handleSave = (mediaName: string) => {
    const payload = {
      name: mediaName,
      mediaItemId: phoneMedia.mediaItemId,
      tenantId: settingsTenantLocation?.phoneTenantId ?? '',
    };

    updateFn(payload);
  };

  const customValidator = (state: ValidatorFieldState<'text'>) => {
    const { value } = state;
    return value.length > maxFileNameLength
      ? t('Cannot exceed {{maxLimit}} characters', { maxLimit: maxFileNameLength })
      : '';
  };

  return (
    <InlineTextEditor
      actionText={t('Save')}
      editEnabled={editEnabled}
      inputFieldLabel={t('Media Name')}
      isSaving={update.isLoading}
      onActionClick={handleSave}
      validator={customValidator}
      value={audioFileName}
      maxLength={maxFileNameLength + 1}
      isFullWidth={true}
    />
  );
};
